import { Card, HStack, HeatGrid, Text, VStack } from '@aj/ui';
import type { HeatCell } from '@aj/ui';
import { useMemo } from 'react';

import { useYearGrid } from '../../data/hooks.js';
import { dateToMs } from '../../lib/dates.js';
import { useI18n, useT } from '../../providers/I18nProvider.js';

/**
 * The year, below the wall chart on Jatra.
 *
 * Not a duplicate of the chart above it — a different question at a different
 * scale. The chart asks "which promise am I keeping?" over six weeks with the
 * habits named; this asks "what did this year look like?" with the habits
 * collapsed away. Neither answers the other's question.
 *
 * It sits below kept-rate and the comeback record on purpose. A year grid is
 * the most impressive thing on the screen and the least actionable: nothing
 * about last March changes what happens tomorrow.
 */
export function YearCard() {
  const t = useT();
  const i18n = useI18n();
  const grid = useYearGrid();

  const weeks = useMemo<HeatCell[][]>(
    () =>
      grid.weeks.map((week) =>
        week.map((cell) => {
          const date = i18n.formatDate(dateToMs(cell.date), {
            day: 'numeric',
            month: 'short',
          });

          return {
            ratio: cell.ratio,
            promised: cell.promised > 0,
            future: cell.future,
            isToday: cell.isToday,
            // Every square says what it is out loud. One hue can't, and a
            // rest day and a missed day are the same hue at a glance.
            label:
              cell.promised === 0
                ? t('year.cellRest', { date })
                : cell.kept === 0
                  ? t('year.cellMissed', { date })
                  : t('year.cell', { date, kept: cell.kept, promised: cell.promised }),
          };
        }),
      ),
    [grid.weeks, i18n, t],
  );

  const months = useMemo(
    () =>
      grid.months.map((month) => ({
        column: month.column,
        label: i18n.formatDate(dateToMs(month.date), { month: 'short' }),
      })),
    [grid.months, i18n],
  );

  return (
    <Card padding={5}>
      <VStack gap={4}>
        <HStack gap={3} align="baseline" justify="between">
          <Text variant="overline" tone="subtle">
            {t('year.title')}
          </Text>
          {/* The one number worth pulling out: days fully kept. Not a streak,
              and not a total of hours — a count of days you did what you said. */}
          {grid.perfectDays > 0 && (
            <Text variant="caption" tone="muted" numeric>
              {t('year.perfect', { count: grid.perfectDays })}
            </Text>
          )}
        </HStack>

        <HeatGrid
          weeks={weeks}
          months={months}
          label={t('year.title')}
          legend={{ low: t('year.legendLow'), high: t('year.legendHigh') }}
        />
      </VStack>
    </Card>
  );
}
